import isNil from 'lodash/isNil'

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
type Try<A1 extends any, A2 extends any, Catch = never> = A1 extends A2 ? A1 : Catch
type Narrowable = string | number | bigint | boolean

type NarrowRaw<A> =
    | (A extends [] ? [] : never)
    | (A extends Narrowable ? A : never)
    | { [K in keyof A]: A[K] extends Function ? A[K] : NarrowRaw<A[K]> }

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
type Narrow<A extends any> = Try<A, [], NarrowRaw<A>>

type _PermPrefixer<T extends string = string> = `ver-${T}`

type _ModulePrefixer<
    Module extends string = string,
    PermName extends string | undefined = undefined,
> = PermName extends string ? _PermPrefixer<`${Module}-${PermName}`> : _PermPrefixer<Module>

type TransaccionesDef<PN extends string = string> = {
    transacciones: {
        [permissionName: string]: PN
    }
}

type MantenimientoDef = {
    mantenimiento: {
        [permissionName: string]: string
    }
}

type DashboardDef = {
    dashboard: {
        [permissionName: string]: string
    }
}

type ConfiguracionDef = {
    configuracion: {
        [permissionName: string]: string
    }
}

type InformesDef = {
    informes: {
        [permisionName: string]: string
    }
}

type ActionesDef = {
    actions: {
        [actionName: string]: string
    }
}

// type Definitions = TransaccionesDef | MantenimientoDef | DashboardDef
type PermissionBaseDef = Partial<TransaccionesDef & MantenimientoDef & DashboardDef & ConfiguracionDef & ActionesDef>

type PermissionDefFn = <
    Resource extends string = string,
    ModName extends string = string,
    PermissionDefs extends PermissionBaseDef = PermissionBaseDef,
>(
    res: Resource,
    moduleName: ModName,
    definitions: Narrow<PermissionDefs>
) => {
    readonly _root: _ModulePrefixer<ModName>
    readonly _resource: Resource
    // } & _DecodedOption<ModName, typeof definitions, TransaccionesDef, 'transacciones'>
} & (typeof definitions extends TransaccionesDef
    ? {
          readonly transacciones: { _perm: _ModulePrefixer<ModName, 'transacciones'> } & {
              readonly [TKey in keyof (typeof definitions)['transacciones']]: (typeof definitions)['transacciones'][TKey] extends infer V
                  ? V extends string
                      ? _ModulePrefixer<ModName, `transacciones-${V}`>
                      : never
                  : never
          }
      }
    : {}) &
    (typeof definitions extends MantenimientoDef
        ? {
              readonly mantenimiento: { _perm: _ModulePrefixer<ModName, 'mantenimiento'> } & {
                  readonly [TKey in keyof (typeof definitions)['mantenimiento']]: (typeof definitions)['mantenimiento'][TKey] extends infer V
                      ? V extends string
                          ? _ModulePrefixer<ModName, `mantenimiento-${V}`>
                          : never
                      : never
              }
          }
        : {}) &
    (typeof definitions extends DashboardDef
        ? {
              readonly dashboard: { _perm: _ModulePrefixer<ModName, 'dashboard'> } & {
                  readonly [TKey in keyof (typeof definitions)['dashboard']]: (typeof definitions)['dashboard'][TKey] extends infer V
                      ? V extends string
                          ? _ModulePrefixer<ModName, `dashboard-${V}`>
                          : never
                      : never
              }
          }
        : {}) &
    (typeof definitions extends ConfiguracionDef
        ? {
              readonly configuracion: { _perm: _ModulePrefixer<ModName, 'configuracion'> } & {
                  readonly [TKey in keyof (typeof definitions)['configuracion']]: (typeof definitions)['configuracion'][TKey] extends infer V
                      ? V extends string
                          ? _ModulePrefixer<ModName, `configuracion-${V}`>
                          : never
                      : never
              }
          }
        : {}) &
    (typeof definitions extends InformesDef
        ? {
              readonly informes: { _perm: _ModulePrefixer<ModName, 'informes'> } & {
                  readonly [TKey in keyof (typeof definitions)['informes']]: (typeof definitions)['informes'][TKey] extends infer V
                      ? V extends string
                          ? _ModulePrefixer<ModName, `informes-${V}`>
                          : never
                      : never
              }
          }
        : {}) &
    (typeof definitions extends ActionesDef
        ? {
              readonly actions: { _perm: _ModulePrefixer<ModName, 'actions'> } & {
                  readonly [TKey in keyof (typeof definitions)['actions']]: (typeof definitions)['actions'][TKey] extends infer V
                      ? V extends string
                          ? _ModulePrefixer<ModName, `actions-${V}`>
                          : never
                      : never
              }
          }
        : {}) & { _print: (asArray: boolean) => void }

// eslint-disable-next-line unused-imports/no-unused-vars
function hasTransaccion<T extends string = string>(obj: PermissionBaseDef): obj is TransaccionesDef {
    return (
        typeof obj === 'object' &&
        !Array.isArray(obj) &&
        Object.hasOwn(obj, 'transacciones') &&
        !isNil((obj as any).transacciones)
    )
}
const _mods = ['transacciones', 'mantenimiento', 'configuracion', 'informes', 'dashboard', 'actions'] as const

const definePermisos: PermissionDefFn = (resource, modName, definition) => {
    const ROOT = `ver-${modName}`
    const rp = <T extends string>(mod: T): `${typeof ROOT}-${T}` => `${ROOT}-${mod}`
    const pt = createPrefixer(ROOT, 'transacciones')
    const pc = createPrefixer(ROOT, 'configuracion')
    const pm = createPrefixer(ROOT, 'mantenimiento')
    const pi = createPrefixer(ROOT, 'informes')
    const pd = createPrefixer(ROOT, 'dashboard')
    const pa = createPrefixer(ROOT, 'actions')

    const _pbytype = {
        transacciones: pt,
        configuracion: pc,
        mantenimiento: pm,
        informes: pi,
        dashboard: pd,
        actions: pa,
    }

    const obj = {
        _root: ROOT,
        _resource: resource,
    }

    _mods.forEach(module => {
        if (!Object.hasOwn(definition, module)) return
        obj[module] = {
            _perm: rp(module),
        }
        Object.entries(definition[module]).forEach(([key, value]) => {
            if (import.meta.env.DEV && !_pbytype[module]) {
                console.error('No se encuentra un descriptor para el modulo:', module)
            }

            // @ts-expect-error
            obj[module][key] = _pbytype[module]?.(value)
        })
    })

    if (import.meta.env.DEV) {
        Object.defineProperty(obj, '_print', {
            enumerable: false,
            writable: false,
            value(asArray: boolean = false) {
                const out = [...Object.entries(this)]
                console.log('Theout', out)
                const result = []

                while (out.length > 0) {
                    // @ts-expect-error
                    const [_key, it] = out.shift()
                    if (_key === '_resource') {
                        console.log('Resource:', it)
                        continue
                    }
                    if (typeof it === 'object') {
                        out.push(...Object.entries(it))
                        continue
                    } else if (typeof it !== 'string') {
                        console.warn(_key, 'is ignored')
                        continue
                    }
                    // @ts-expect-error
                    result.push(it)
                }
                if (asArray) console.dir(result)
                else console.debug(result.join('\n'))
            },
        })
    } else {
        Object.defineProperty(obj, '_print', {
            value() {},
        })
    }
    return obj as any
}

type PrefixerFn<RP extends string, M extends string> = <T extends string>(permission: T) => `${RP}-${M}-${T}`
const createPrefixer =
    <RP extends string, Module extends string>(rootPermission: RP, mod: Module): PrefixerFn<RP, Module> =>
    <T extends string>(value: T) =>
        `${rootPermission}-${mod}-${value}`

export { definePermisos }
